
"use client"
import { getSiteInfoClient } from "@/lib/site-info-client"
import type React from "react"
import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { supabaseClient } from "@/lib/supabase/client"
import { Spinner } from "@/components/ui/spinner"

interface LoginClientProps {
  logoUrl: string
}

export default function LoginClient({ logoUrl }: LoginClientProps) {
  // Novo fluxo de recuperação de senha
  const [showForgot, setShowForgot] = useState(false)
  const [forgotStep, setForgotStep] = useState<'email' | 'code' | 'reset'>('email')
  const [forgotEmail, setForgotEmail] = useState("")
  const [forgotMessage, setForgotMessage] = useState<string | null>(null)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState("")
  const [codeError, setCodeError] = useState<string | null>(null)
  const [resetPassword, setResetPassword] = useState("")
  const [resetPassword2, setResetPassword2] = useState("")
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSuccess, setResetSuccess] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loginSuccess, setLoginSuccess] = useState(false)
  const router = useRouter()

  // Fluxo simples de esqueci minha senha
  // Novo fluxo: solicitar código
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotMessage(null)
    setForgotLoading(true)
    try {
      const resp = await fetch("/api/auth/send-recovery-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail })
      })
      const data = await resp.json()
      if (!resp.ok) {
        setForgotMessage(data.error || "Erro ao enviar código. Tente novamente.")
      } else {
        setForgotMessage("Enviamos um código de recuperação para seu e-mail.")
        setForgotStep('code')
      }
    } catch (err) {
      setForgotMessage("Erro ao processar solicitação. Tente novamente.")
    } finally {
      setForgotLoading(false)
    }
  }

  // Novo fluxo: validar código
  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setCodeError(null)
    setForgotLoading(true)
    try {
      const resp = await fetch("/api/auth/validate-recovery-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail, code: recoveryCode })
      })
      const data = await resp.json()
      if (!resp.ok) {
        setCodeError(data.error || "Código inválido ou expirado.")
      } else {
        setForgotStep('reset')
      }
    } catch (err) {
      setCodeError("Erro ao validar código. Tente novamente.")
    } finally {
      setForgotLoading(false)
    }
  }

  // Novo fluxo: redefinir senha
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetError(null)
    setForgotLoading(true)
    if (resetPassword.length < 6) {
      setResetError("A senha deve ter pelo menos 6 caracteres.")
      setForgotLoading(false)
      return
    }
    if (resetPassword !== resetPassword2) {
      setResetError("As senhas não coincidem.")
      setForgotLoading(false)
      return
    }
    try {
      const resp = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail, code: recoveryCode, newPassword: resetPassword })
      })
      const data = await resp.json()
      if (!resp.ok) {
        setResetError(data.error || "Erro ao redefinir senha.")
      } else {
        setResetSuccess(true)
        setForgotStep('email')
        setShowForgot(false)
      }
    } catch (err) {
      setResetError("Erro ao redefinir senha. Tente novamente.")
    } finally {
      setForgotLoading(false)
    }
  }

  // Limpar o sessionStorage ao carregar a página de login
  useEffect(() => {
    // Limpar apenas se não estiver vindo de um redirecionamento do AuthGuard
    if (!sessionStorage.getItem("redirected_from_auth_guard")) {
      console.log("Limpando dados de sessão anteriores")
      sessionStorage.removeItem("is_authenticated")
      sessionStorage.removeItem("userEmail")
      sessionStorage.removeItem("userName")
    } else {
      sessionStorage.removeItem("redirected_from_auth_guard")
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      console.log("Tentando login com:", email)

      // Primeiro, tentar autenticar com Supabase Auth
      try {
        const { data: authData, error: authError } = await supabaseClient.auth.signInWithPassword({
          email,
          password,
        })

        if (!authError && authData.user) {
          console.log("Login com Supabase Auth bem-sucedido")

          // Armazenar informações do usuário no sessionStorage
          sessionStorage.setItem("is_authenticated", "true")
          sessionStorage.setItem("userEmail", email)

          // Buscar nome do usuário se disponível
          try {
            const { data: userData } = await supabaseClient
              .from("admin_users")
              .select("nome")
              .eq("email", email)
              .single()

            if (userData?.nome) {
              sessionStorage.setItem("userName", userData.nome)
            } else {
              sessionStorage.setItem("userName", email.split("@")[0])
            }
          } catch (e) {
            console.log("Erro ao buscar nome do usuário, usando email como fallback")
            sessionStorage.setItem("userName", email.split("@")[0])
          }

          setLoginSuccess(true)
          setTimeout(() => router.push("/admin"), 500)
          return
        }

        // Se chegou aqui, o login com Supabase Auth falhou
        console.log("Login com Supabase Auth falhou, tentando verificar na tabela admin_users")
      } catch (authError) {
        console.log("Erro ao tentar login com Supabase Auth:", authError)
      }

      // Se o login com Supabase Auth falhou, verificar na tabela admin_users
      // Usar função RPC que bypassa RLS
      const { data: isValid, error: rpcError } = await (supabaseClient as any).rpc("verify_password_by_email", {
        email_input: email,
        password_input: password,
      })

      console.log("Resultado da verificação RPC:", { isValid, rpcError })

      if (rpcError) {
        console.error("Erro RPC:", rpcError)
        throw new Error(`Erro na verificação de credenciais: ${rpcError.message}`)
      }

      if (isValid === true) {
        console.log("Credenciais válidas, buscando informações do usuário...")

        // Buscar informações do usuário
        // IMPORTANTE: Esta query pode falhar se houver RLS ativo
        // Mas como o usuário existe (verificado pela RPC), vamos usar um try-catch
        let userName = email.split("@")[0] // fallback

        try {
          const { data: userData, error: userError } = await supabaseClient
            .from("admin_users")
            .select("id, email, nome")
            .eq("email", email)
            .single()

          if (!userError && userData?.nome) {
            userName = userData.nome
          }
        } catch (e) {
          console.log("Não foi possível buscar nome do usuário, usando email como fallback")
        }

        // Armazenar informações do usuário no sessionStorage de forma consistente
        sessionStorage.setItem("is_authenticated", "true")
        sessionStorage.setItem("userEmail", email)
        sessionStorage.setItem("userName", userName)

        console.log("Login bem-sucedido, dados armazenados no sessionStorage")

        // Indicar que o login foi bem-sucedido
        setLoginSuccess(true)

        // Pequeno atraso para garantir que o sessionStorage seja atualizado
        setTimeout(() => {
          console.log("Redirecionando para /admin")
          router.push("/admin")
        }, 500)
      } else {
        setError("Credenciais inválidas")
      }
    } catch (error) {
      console.error("Erro de login:", error)
      setError(`Erro de login: ${error instanceof Error ? error.message : "Credenciais inválidas"}`)
    } finally {
      setIsLoading(false)
    }
  }

  // Se o login foi bem-sucedido, mostrar mensagem de redirecionamento
  if (loginSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-6">
            <Spinner size="lg" className="mb-4" />
            <h2 className="text-xl font-semibold mb-2">Login bem-sucedido!</h2>
            <p className="text-center text-gray-600">Redirecionando para a área administrativa...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <Image src={logoUrl || "/placeholder.svg"} alt="Logo" width={150} height={60} className="object-contain" />
          </div>
          <CardTitle className="text-2xl">Área Administrativa</CardTitle>
          <CardDescription>Entre com suas credenciais para acessar o painel</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {showForgot ? (
            <div>
              {forgotStep === 'email' && (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={forgotLoading}>
                    {forgotLoading ? "Enviando..." : "Enviar código de recuperação"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => setShowForgot(false)}>
                    Voltar para login
                  </Button>
                  {forgotMessage && <div className="text-center text-sm mt-2">{forgotMessage}</div>}
                </form>
              )}
              {forgotStep === 'code' && (
                <form onSubmit={handleValidateCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="recovery-code">Código recebido</Label>
                    <Input
                      id="recovery-code"
                      type="text"
                      placeholder="Digite o código"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                      required
                      maxLength={6}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={forgotLoading}>
                    {forgotLoading ? "Validando..." : "Validar código"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => { setForgotStep('email'); setForgotMessage(null); }}>
                    Voltar
                  </Button>
                  {codeError && <div className="text-center text-sm mt-2 text-red-500">{codeError}</div>}
                </form>
              )}
              {forgotStep === 'reset' && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-password">Nova senha</Label>
                    <Input
                      id="reset-password"
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reset-password2">Confirme a nova senha</Label>
                    <Input
                      id="reset-password2"
                      type="password"
                      value={resetPassword2}
                      onChange={(e) => setResetPassword2(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={forgotLoading}>
                    {forgotLoading ? "Redefinindo..." : "Redefinir senha"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => { setForgotStep('email'); setForgotMessage(null); }}>
                    Voltar
                  </Button>
                  {resetError && <div className="text-center text-sm mt-2 text-red-500">{resetError}</div>}
                  {resetSuccess && <div className="text-center text-green-600 mt-2">Senha redefinida com sucesso! Faça login.</div>}
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={handleLogin}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Spinner size="sm" className="mr-2" /> Entrando...
                    </span>
                  ) : (
                    "Entrar"
                  )}
                </Button>
                <Button type="button" variant="link" className="w-full mt-2" onClick={() => setShowForgot(true)}>
                  Esqueci minha senha
                </Button>
              </div>
            </form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-gray-500">Área restrita aos administradores do site</p>
        </CardFooter>
      </Card>
    </div>
  )
}